import { useCallback, useState } from "react";
import { Box } from "@mui/material";
import { Alert, Typography } from "@platform/design-system";

import { useCarrierRepsQuery } from "../hooks";

import { CarrierRepDialog } from "./CarrierRepDialog";
import { CarrierRepsFilters } from "./CarrierRepsFilters";
import { CarrierRepsTable } from "./CarrierRepsTable";

import type { Carrier, CarrierRep } from "../types";

interface CarrierRepsTabProps {
  carrier: Carrier;
}

interface CarrierRepsProps extends CarrierRepsTabProps {
  onEditRep: (rep: CarrierRep) => void;
}

const CarrierReps = ({
  carrier,
  onEditRep,
}: CarrierRepsProps): React.JSX.Element => {
  const { repList, loading, error } = useCarrierRepsQuery(carrier.id);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading || !repList) {
    return (
      <Typography variant="body-medium" color="textSecondary">
        Loading reps...
      </Typography>
    );
  }

  return <CarrierRepsTable reps={repList.reps} onEditRep={onEditRep} />;
};

interface RepDialogState {
  rep?: CarrierRep;
}

export const CarrierRepsTab = ({
  carrier,
}: CarrierRepsTabProps): React.JSX.Element => {
  const [repDialog, setRepDialog] = useState<RepDialogState | null>(null);
  const openEditDialog = useCallback(
    (rep: CarrierRep) => setRepDialog({ rep }),
    [],
  );

  return (
    <Box
      data-testid="carrier-reps-tab"
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <CarrierRepsFilters onAddRep={() => setRepDialog({})} />
      <CarrierReps carrier={carrier} onEditRep={openEditDialog} />
      {repDialog && (
        <CarrierRepDialog
          carrier={carrier}
          rep={repDialog.rep}
          onClose={() => setRepDialog(null)}
        />
      )}
    </Box>
  );
};
